/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { AccountTypes, AccountUsageTypes, DepositTypes, Merchant } from "../../../../models";
import { MerchantService } from "../../../../services";

@Component({
  selector: "app-account-details-edit",
  templateUrl: "./account-details-edit.component.html"
})
export class AccountDetailsEditComponent implements OnInit {
  accountUsage = AccountUsageTypes;
  accountTypes = AccountTypes;
  depositTypes = DepositTypes;
  FUNDING_DEPOSITS = "FundingDeposits";
  FEE_WITHDRAWALS = "FeeWithdrawals";
  separateFees = false;
  @Input() parentGroup!: FormGroup;
  @Input("merchant") merchant!: Merchant;

  constructor(private merchantService: MerchantService) {}

  ngOnInit(): void {
    this.separateFees = this.merchant.accountDetails.separateFundingAccounts;
  }

  isWorldPay(): boolean {
    return this.merchantService.isWorldPay(this.merchant);
  }

  accountUsageChange(event: any): void {
    if (event.target.id === "accountUsage") {
      if (event.target.value === this.accountUsage.Combined) {
        this.separateFees = false;
        this.otherAccountDetailsFormControls.accountUsage.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountUsage.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountType.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountType.updateValueAndValidity();
        this.otherAccountDetailsFormControls.routingNumber.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.routingNumber.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountNumber.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountNumber.updateValueAndValidity();
      } else if (event.target.value === this.FEE_WITHDRAWALS) {
        this.separateFees = true;
        this.otherAccountDetailsFormControls.accountUsage.setValue(this.FUNDING_DEPOSITS);
        this.otherAccountDetailsFormControls.accountUsage.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountUsage.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountType.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountType.updateValueAndValidity();
        this.otherAccountDetailsFormControls.routingNumber.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.routingNumber.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountNumber.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountNumber.updateValueAndValidity();
      } else if (event.target.value === this.FUNDING_DEPOSITS) {
        this.separateFees = true;
        this.otherAccountDetailsFormControls.accountUsage.setValue(this.FEE_WITHDRAWALS);
        this.otherAccountDetailsFormControls.accountUsage.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountUsage.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountType.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountType.updateValueAndValidity();
        this.otherAccountDetailsFormControls.routingNumber.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.routingNumber.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountNumber.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountNumber.updateValueAndValidity();
      }
    } else if (event.target.id === "accountUsage2") {
      if (event.target.value === this.accountUsage.Combined) {
        this.swapAccountDetails();
        this.separateFees = false;
        this.otherAccountDetailsFormControls.accountUsage.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountUsage.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountType.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountType.updateValueAndValidity();
        this.otherAccountDetailsFormControls.routingNumber.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.routingNumber.updateValueAndValidity();
        this.otherAccountDetailsFormControls.accountNumber.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.accountNumber.updateValueAndValidity();
      } else if (event.target.value === this.FUNDING_DEPOSITS) {
        this.separateFees = true;
        this.accountDetailsFormControls.accountUsage.setValue(this.FEE_WITHDRAWALS);
      } else if (event.target.value === this.FEE_WITHDRAWALS) {
        this.separateFees = true;
        this.accountDetailsFormControls.accountUsage.setValue(this.FUNDING_DEPOSITS);
      }
    }
  }

  resetDepositType(event: any): void {
    if (event.target.id === "accountType") {
      if (event.target.value === "S") {
        this.accountDetailsFormControls.checkingDepositType.setValue("");
        this.accountDetailsFormControls.checkingDepositType.removeValidators(Validators.required);
        this.accountDetailsFormControls.checkingDepositType.updateValueAndValidity();
      } else if (
        event.target.value === "C" &&
        this.accountDetailsFormControls.accountUsage.value === this.FUNDING_DEPOSITS
      ) {
        this.accountDetailsFormControls.checkingDepositType.addValidators(Validators.required);
        this.accountDetailsFormControls.checkingDepositType.updateValueAndValidity();
      }
    } else if (event.target.id === "accountType2") {
      if (event.target.value === "S") {
        this.otherAccountDetailsFormControls.checkingDepositType.setValue("");
        this.otherAccountDetailsFormControls.checkingDepositType.removeValidators(Validators.required);
        this.otherAccountDetailsFormControls.checkingDepositType.updateValueAndValidity();
      } else if (
        event.target.value === "C" &&
        this.otherAccountDetailsFormControls.accountUsage.value === this.FUNDING_DEPOSITS
      ) {
        this.otherAccountDetailsFormControls.checkingDepositType.addValidators(Validators.required);
        this.otherAccountDetailsFormControls.checkingDepositType.updateValueAndValidity();
      }
    }
  }

  swapAccountDetails() {
    this.accountDetailsFormControls.accountUsage.setValue(this.otherAccountDetailsFormControls.accountUsage.value);
    this.accountDetailsFormControls.accountType.setValue(this.otherAccountDetailsFormControls.accountType.value);
    this.accountDetailsFormControls.checkingDepositType.setValue(
      this.otherAccountDetailsFormControls.checkingDepositType.value
    );
    this.accountDetailsFormControls.routingNumber.setValue(this.otherAccountDetailsFormControls.routingNumber.value);
    this.accountDetailsFormControls.accountNumber.setValue(this.otherAccountDetailsFormControls.accountNumber.value);
  }

  get otherAccountDetailsFormControls() {
    const x: FormGroup = this.parentGroup.get("otherAccountDetails") as FormGroup;
    return x.controls;
  }

  get accountDetailsFormControls() {
    const x: FormGroup = this.parentGroup.get("accountDetails") as FormGroup;
    return x.controls;
  }
}
